import React, { useContext, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { SidebarCollapseContext } from './SidebarCollapseContext';
import { routeMatches } from './routeMatches';
import SidebarFlyout from './SidebarFlyout';
import SidebarIcon from './SidebarIcon';

interface IProps {
  label: string;
  icon?: string;
  iconSrc?: string;
  to?: string;
  children?: React.ReactNode;
}

export default function SidebarSection({ label, icon, iconSrc, to, children }: IProps) {
  const { collapsed } = useContext(SidebarCollapseContext);
  const path = useLocation().pathname;
  const hasChildren = React.Children.count(children) > 0;

  const childItems = React.Children.toArray(children)
    .filter(React.isValidElement)
    .map(child => child.props as { to?: string; href?: string; label: string });
  const childRoutes = childItems.map(child => child.to);
  const headerOwnsHighlight = !to || !childRoutes.includes(to);

  const groupRoutes = [to, ...childRoutes].filter((route): route is string => !!route);
  const isActive = routeMatches(path, ...groupRoutes);

  const [expanded, setExpanded] = useState(isActive);

  const onLanding = !!to && routeMatches(path, to);
  const headerIsCurrentPage = onLanding && headerOwnsHighlight;
  const locked = isActive && hasChildren && !headerIsCurrentPage;
  const isOpen = locked || expanded;

  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  const toggle = () => { if (!locked) setExpanded(prev => !prev); };
  const toggleFromChevron = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggle();
  };

  if (collapsed) {
    const ownsLanding = !!to && headerOwnsHighlight;
    return (
      <SidebarFlyout
        flyoutKey={to ?? label}
        label={label}
        icon={icon ?? 'three-dots-vertical'}
        iconSrc={iconSrc}
        to={to}
        active={isActive}
        header={ownsLanding ? undefined : label}
        items={ownsLanding
          ? [{ to, label, parent: true }, ...childItems]
          : childItems} />
    );
  }

  const navigableHeader =
    <NavLink
      to={to!}
      className={({ isActive }) => `nav-link rounded-2 mx-1 px-3 d-flex align-items-center justify-content-between${isActive && headerOwnsHighlight ? ' active' : ''}`}>
      <span className="d-flex align-items-center"><SidebarIcon icon={icon} iconSrc={iconSrc} />{label}</span>
      {hasChildren && !locked &&
        <i
          className={`bi-chevron-${isOpen ? 'down' : 'right'} sidebar-section-chevron`}
          style={{ fontSize: '0.7rem', padding: '0.3rem 0.5rem', margin: '-0.3rem -0.5rem -0.3rem 0', borderRadius: '0.375rem' }}
          role="button"
          tabIndex={0}
          aria-label={`Toggle ${label}`}
          aria-expanded={isOpen}
          onClick={toggleFromChevron}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') toggleFromChevron(e); }}></i>}
    </NavLink>;

  const toggleHeader =
    <button
      type="button"
      className="nav-link rounded-2 mx-1 bg-transparent text-start w-100 px-3 d-flex align-items-center"
      onClick={toggle}
      aria-label={label}
      aria-expanded={isOpen}>
      <i className={`bi-chevron-${isOpen ? 'down' : 'right'} nav-icon`} style={{ flexBasis: '1.5rem' }}></i>
      {iconSrc
        ? <img className="me-2" style={{ opacity: 0.7 }} src={iconSrc} alt="" width={16} height={16} />
        : icon && <i className={`bi-${icon} me-2`} style={{ opacity: 0.7 }}></i>}
      {label}
    </button>;

  return (
    <li className="nav-item" style={{ marginBottom: '0.125rem' }}>
      {to ? navigableHeader : toggleHeader}
      {hasChildren &&
        <ul className={`nav flex-column sidebar-section-items collapse${isOpen ? ' show' : ''}`} style={{ marginLeft: '1.9375rem' }}>
          {children}
        </ul>}
    </li>
  );
}
